export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  VIEWER: 'viewer',
  SALES_USER: 'sales_user',
} as const;

export type KnownUserRole = typeof USER_ROLES[keyof typeof USER_ROLES];
export type UserRole = KnownUserRole | (string & {});

export type RolePermission =
  | 'viewHome'
  | 'accessSetup'
  | 'viewMetaAdAccounts'
  | 'viewAdAnalytics'
  | 'viewBotAnalytics'
  | 'manageConversionEvents'
  | 'manageRetargeting'
  | 'viewCampaignDashboard'
  | 'viewLeads'
  | 'viewBillingReport'
  | 'viewAudience'
  | 'viewSettings'
  | 'viewConfig'
  | 'selectMetaAdAccountInReports'
  | 'viewPlatformBudget'
  | 'manageLeadDistribution';

type RoleConfig = {
  value: KnownUserRole;
  label: string;
  tagColor: string;
  selectable: boolean;
  permissions: RolePermission[];
};

const ADMIN_REPORTING_PERMISSIONS: RolePermission[] = [
  'viewHome',
  'viewMetaAdAccounts',
  'viewAdAnalytics',
  'viewBotAnalytics',
  'manageConversionEvents',
  'manageRetargeting',
  'viewCampaignDashboard',
  'viewLeads',
  'viewBillingReport',
  'viewAudience',
  'viewSettings',
  'selectMetaAdAccountInReports',
];

export const ROLE_CONFIG: Record<KnownUserRole, RoleConfig> = {
  [USER_ROLES.SUPER_ADMIN]: {
    value: USER_ROLES.SUPER_ADMIN,
    label: 'Super Admin',
    tagColor: 'purple',
    selectable: false,
    permissions: [
      ...ADMIN_REPORTING_PERMISSIONS,
      'accessSetup',
      'viewConfig',
      'viewPlatformBudget',
      'manageLeadDistribution',
    ],
  },
  [USER_ROLES.ADMIN]: {
    value: USER_ROLES.ADMIN,
    label: 'Admin',
    tagColor: 'red',
    selectable: true,
    permissions: [
      ...ADMIN_REPORTING_PERMISSIONS,
      'accessSetup',
      'viewConfig',
      'viewPlatformBudget',
      'manageLeadDistribution',
    ],
  },
  [USER_ROLES.MANAGER]: {
    value: USER_ROLES.MANAGER,
    label: 'Manager',
    tagColor: 'gold',
    selectable: true,
    permissions: [
      'viewHome',
      'viewMetaAdAccounts',
      'viewAdAnalytics',
      'viewBotAnalytics',
      'manageConversionEvents',
      'manageRetargeting',
      'viewCampaignDashboard',
      'viewLeads',
      'viewBillingReport',
      'viewAudience',
      'viewSettings',
    ],
  },
  [USER_ROLES.VIEWER]: {
    value: USER_ROLES.VIEWER,
    label: 'Viewer',
    tagColor: 'geekblue',
    selectable: true,
    permissions: [
      'viewHome',
      'viewAdAnalytics',
      'viewBotAnalytics',
      'viewLeads',
    ],
  },
  [USER_ROLES.SALES_USER]: {
    value: USER_ROLES.SALES_USER,
    label: 'Sales User',
    tagColor: 'cyan',
    selectable: true,
    permissions: [
      'viewHome',
      'viewAdAnalytics',
      'viewBillingReport',
      'selectMetaAdAccountInReports',
    ],
  },
};

export const ROLE_OPTIONS = Object.values(ROLE_CONFIG).filter((role) => role.selectable);

export const normalizeRole = (role?: string): UserRole => {
  return (role || USER_ROLES.VIEWER).toLowerCase() as UserRole;
};

export const isKnownRole = (role?: string): role is KnownUserRole => {
  return Boolean(role && Object.prototype.hasOwnProperty.call(ROLE_CONFIG, role));
};

export const getRoleConfig = (role?: string): RoleConfig | undefined => {
  const normalizedRole = normalizeRole(role);
  return isKnownRole(normalizedRole) ? ROLE_CONFIG[normalizedRole] : undefined;
};

export const getRoleLabel = (role?: string): string => {
  const config = getRoleConfig(role);
  if (config) return config.label;

  return normalizeRole(role)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const getRoleTagColor = (role?: string): string => {
  return getRoleConfig(role)?.tagColor || 'blue';
};

export const hasRolePermission = (role: string | undefined, permission: RolePermission): boolean => {
  return getRoleConfig(role)?.permissions.includes(permission) || false;
};
